import React, { useEffect, useState } from "react";
import { useDebouncedCallback } from "@mantine/hooks";
import Iframe from "../shared/Iframe";
import iframeStyle from '../shared/Iframe.module.css';

type MediaObject = ImageMedia | VideoMedia | AudioMedia | RichMedia | undefined;

interface ImageMedia {
  type: 'image';
  url: string;
  html?: string;
  width?: number;
  height?: number;
  size?: number;
}

interface VideoMedia {
  type: 'video';
  url: string;
  html?: string;
  width?: number;
  height?: number;
  size?: number;
  duration?: number;
  medium?: string;
}

interface AudioMedia {
  type: 'audio';
  url: string;
  html?: string;
  size?: number;
  duration?: number;
}

interface RichMedia {
  type: 'rich';
  html: string;
  width?: number;
  height?: number;
  provider_name?: string;
}

interface PreviewData {
  original_url: string;
  url: string;
  type: 'html' | 'text' | 'image' | 'video' | 'audio' | 'rss' | 'xml' | 'atom' | 'json' | 'ppt' | 'link' | 'error';
  cache_age?: number;
  safe?: boolean;
  provider_name?: string;
  provider_url?: string;
  provider_display?: string;
  favicon_url?: string;
  favicon_colors?: Array<{ color: string; weight: number }>;
  title?: string;
  authors?: Array<{ name: string; url?: string }>;
  published?: number;
  offset?: number;
  description?: string;
  lead?: string;
  media?: MediaObject;
  content?: string;
  entities?: Array<{
    name: string;
    type: string;
    confidence: number;
  }>;
  keywords?: Array<string>;
  language?: string;
  app_links?: Array<{
    app_name: string;
    app_store_id?: string;
    app_url?: string;
  }>;
  feeds?: Array<string>;
  links?: Array<{
    url: string;
    title?: string;
  }>;
  images?: ImageMedia[];
  videos?: VideoMedia[];
  audios?: AudioMedia[];
  error_code?: number;
  error_message?: string;  
}

type LinkPostPreviewProps = {
    src: string;
    embedHtml?: string;
    setEmbedHtml: React.Dispatch<React.SetStateAction<string>>;
    setParsedTitle: React.Dispatch<React.SetStateAction<string>>;
}

export default function LinkPostPreview({
    src,
    embedHtml,
    setEmbedHtml,
    setParsedTitle
}: LinkPostPreviewProps) {
  // const [url, setUrl] = useState<string>(src);
  const [preview, setPreview] = useState<PreviewData | {}>({});
  const [embedlyUrl, setEmbedlyUrl] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [url, setUrl] = useState<URL | undefined>(undefined);

  const EMBEDLY_API_KEY: string = import.meta.env.VITE_EMBEDLY_API_KEY;
  
  const fetchPreview = useDebouncedCallback(async () => {
    try {
      setError("");
      const response = await fetch(
        `https://api.embed.ly/1/extract?url=${encodeURIComponent(src)}&key=${EMBEDLY_API_KEY}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch preview.");
      }

      const data: PreviewData = await response.json();
      console.log(data);
      setPreview(data);
      setParsedTitle(data?.title || '');
      if (data.media?.html) {
        setEmbedHtml(data.media.html || '');
      }
    } catch (err) {
      setError("Could not fetch preview. Please check the URL or try again.");
    }
  }, 400);

  function extractYouTubeVideoId(url: string): string | null | undefined {
    const match = url.match(
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
    );
    return match ? match[1] : null;
  }

  async function getYouTubeTitle(url: string): Promise<string | null> {
    
    const videoId = extractYouTubeVideoId(url);
    if (!videoId) return null;
  
    const apiUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
  
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch video data')
      };
      const data = await response.json();
      return data.title;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  useEffect(() => {
    if (src) {
      try {
        setUrl(new URL(src));
      } catch (error) {
        setUrl(undefined);
        return;
      }
    } else {
      setUrl(undefined);
    }
  }, [src]);

  useEffect(() => {
    const setYouTubeTitle = async (url: string) => {
      const title = await getYouTubeTitle(url);
      if (title) {
        setParsedTitle(title);
      }
    }

    if (url && (url.hostname === 'www.youtube.com' || url.hostname === 'youtu.be')) {
      setYouTubeTitle(url.href);
      return;
    }
    const hasUpdated = embedlyUrl !== src;
    if (!embedHtml || hasUpdated) {
      fetchPreview();
      setEmbedlyUrl(src);
    }
  }, [url]);

  if (url && (url.hostname === 'www.youtube.com' || url.hostname === 'youtu.be')) {
    return <Iframe url={url} />
  }

  if (embedHtml && (embedHtml?.includes('iframe') || embedHtml?.includes('blockquote'))) {
    return <Iframe embed_html={embedHtml} />
  }

  return (
    <></>
  );
};
